// features/settings/pages/TaxesPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    Receipt, Plus, Save, Pencil, Trash2, Star, Percent, Calculator,
    ArrowLeft, TrendingUp, Info,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { clsx } from 'clsx';

interface Tax {
    id: string;
    name: string;
    code: string;
    rate: number;
    type: 'percentage' | 'fixed';
    isCompound: boolean;
    isDefault: boolean;
    isActive: boolean;
    description: string | null;
}

export function TaxesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingTax, setEditingTax] = useState<Tax | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Tax | null>(null);
    const [form, setForm] = useState({
        name: '', code: '', rate: '', type: 'percentage',
        description: '', isDefault: false, isCompound: false,
    });

    const { data: taxes, isLoading, refetch } = useQuery<Tax[]>({
        queryKey: ['taxes', user?.tenantId],
        queryFn: () => api.getTaxes(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingTax
            ? api.updateTax(user!.tenantId, editingTax.id, payload)
            : api.createTax(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['taxes'] });
            addToast({ type: 'success', title: editingTax ? 'Tax updated' : 'Tax created' });
            closeForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.deleteTax(user!.tenantId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['taxes'] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Tax deleted' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const setDefaultMutation = useMutation({
        mutationFn: (id: string) => api.setDefaultTax(user!.tenantId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['taxes'] });
            addToast({ type: 'success', title: 'Default tax updated' });
        },
    });

    const handleEdit = (tax: Tax) => {
        setEditingTax(tax);
        setForm({
            name: tax.name, code: tax.code, rate: String(tax.rate),
            type: tax.type, description: tax.description || '',
            isDefault: tax.isDefault, isCompound: tax.isCompound,
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingTax(null);
        setForm({ name: '', code: '', rate: '', type: 'percentage', description: '', isDefault: false, isCompound: false });
    };

    const formatRate = (tax: Tax) => tax.type === 'percentage' ? `${tax.rate}%` : `₦${tax.rate.toLocaleString()}`;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-teal-200 border-t-teal-600" />
            </div>
        );
    }

    const defaultTax = (taxes || []).find(t => t.isDefault);
    const activeCount = (taxes || []).filter(t => t.isActive).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/25">
                                <Receipt className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Tax Configuration</h1>
                                <p className="text-sm text-slate-500">
                                    {taxes?.length || 0} tax rate{(taxes?.length || 0) !== 1 ? 's' : ''} · {activeCount} active · Default: {defaultTax?.name || 'None'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => { closeForm(); setShowForm(true); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all font-semibold shadow-lg shadow-teal-500/25 text-sm"
                            type="button"
                        >
                            <Plus className="w-4 h-4" /> Add Tax Rate
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Taxes', value: taxes?.length || 0, icon: Receipt, gradient: 'from-teal-500 to-cyan-600' },
                        { label: 'Active Rates', value: activeCount, icon: TrendingUp, gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Default Tax', value: defaultTax?.code || '—', icon: Star, gradient: 'from-amber-500 to-orange-600' },
                        { label: 'Compound Taxes', value: (taxes || []).filter(t => t.isCompound).length, icon: Calculator, gradient: 'from-purple-500 to-violet-600' },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300">
                            <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</p>
                                </div>
                                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                                    <stat.icon className="w-5 h-5 text-white" />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {(!taxes || taxes.length === 0) ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-teal-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Receipt className="w-10 h-10 text-teal-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No taxes configured</h3>
                        <p className="text-slate-400 mt-2 max-w-md mx-auto">
                            Set up tax rates like VAT for your sales transactions.
                        </p>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:from-teal-600 transition-all font-semibold shadow-lg inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Add First Tax
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(taxes || []).map((tax, i) => (
                            <motion.div key={tax.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -4 }}
                                className={clsx(
                                    'group relative bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden',
                                    tax.isDefault ? 'border-teal-400 shadow-lg shadow-teal-500/10' : 'border-slate-200 shadow-sm hover:shadow-lg'
                                )}>
                                {tax.isDefault && <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-600" />}
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={clsx(
                                                'w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110',
                                                tax.isDefault ? 'bg-gradient-to-br from-teal-500 to-cyan-600 shadow-md' : 'bg-slate-100'
                                            )}>
                                                {tax.type === 'percentage' ? (
                                                    <Percent size={22} className={tax.isDefault ? 'text-white' : 'text-slate-500'} />
                                                ) : (
                                                    <Calculator size={22} className={tax.isDefault ? 'text-white' : 'text-slate-500'} />
                                                )}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold text-slate-900">{tax.name}</h3>
                                                    {tax.isDefault && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                                </div>
                                                <code className="text-xs text-slate-400 font-mono">{tax.code}</code>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {tax.isCompound && <Badge variant="info" size="sm">Compound</Badge>}
                                            <Badge variant={tax.isActive ? 'success' : 'danger'} size="sm">
                                                {tax.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="text-center py-4 bg-slate-50 rounded-xl mb-3">
                                        <p className="text-3xl font-bold text-slate-900">{formatRate(tax)}</p>
                                        {tax.description && <p className="text-xs text-slate-400 mt-1">{tax.description}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEdit(tax)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                                            <Pencil size={12} /> Edit
                                        </button>
                                        {!tax.isDefault && (
                                            <button onClick={() => setDefaultMutation.mutate(tax.id)}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                                                <Star size={12} /> Default
                                            </button>
                                        )}
                                        <button onClick={() => setDeleteConfirm(tax)}
                                            className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingTax ? 'Edit Tax Rate' : 'Add Tax Rate'}
                    subtitle="Configure tax rate and rules"
                    icon={Receipt}
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
                            <Button
                                leftIcon={<Save size={16} />}
                                onClick={() => saveMutation.mutate({ ...form, rate: parseFloat(form.rate) || 0 })}
                                isLoading={saveMutation.isPending}
                                disabled={!form.name || !form.code || !form.rate}
                            >
                                {editingTax ? 'Update Tax' : 'Save Tax'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Tax Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Value Added Tax" required />
                            <Input label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VAT" required />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Rate *" type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="7.50" required />
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Type</label>
                                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20">
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount (₦)</option>
                                </select>
                            </div>
                        </div>
                        <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Standard VAT rate" />
                        <div className="space-y-3 p-3 bg-slate-50 rounded-xl">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.isCompound} onChange={(e) => setForm({ ...form, isCompound: e.target.checked })}
                                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                                <div>
                                    <span className="text-sm font-medium">Compound Tax</span>
                                    <p className="text-xs text-slate-400">Applied on top of other taxes</p>
                                </div>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                                <div>
                                    <span className="text-sm font-medium">Set as Default Tax</span>
                                    <p className="text-xs text-slate-400">Used as the primary tax rate for sales</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </SettingsModal>

                {/* Delete Confirmation Modal */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                        <Trash2 className="w-8 h-8 text-rose-500" />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900">Delete "{deleteConfirm.name}"?</h2>
                                    <p className="text-sm text-slate-500 mt-2">
                                        This tax rate will be permanently removed. Sales using this rate will not be affected.
                                    </p>
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button onClick={() => setDeleteConfirm(null)}
                                        className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm">Cancel</button>
                                    <button onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        disabled={deleteMutation.isPending}
                                        className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25">
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}